import { useMemo } from "react";

import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

import { getDiscountPriceFor } from "utils/planUtils";

import { Coupon } from "types/Coupons";
import Plan from "types/Plan";


function mergeCoupons(coupons: Coupon[] | null | undefined, couponsToMerge: Coupon | Coupon[] | null | undefined): Coupon[] | undefined {
  const couponsToMergeArray = Array.isArray(couponsToMerge) ? couponsToMerge : (couponsToMerge ? [ couponsToMerge ] : undefined);
  let mergedCoupons = coupons ? [ ...coupons ] : undefined;

  for (const coupon of couponsToMergeArray || []) {
    mergedCoupons = (mergedCoupons || [])
      .filter(existing => existing.discount_type !== coupon.discount_type)
      .concat(coupon);
  }

  return mergedCoupons;
}

type UseDiscountPriceArgs = {
  useScheduledSubscriptionIfAny?: boolean;
  coupon?: Coupon;
  targetPlan?: Pick<Plan, "planSlug" | "price" | "defaultCoupons">;
  /** Skip active subscription coupons — only use defaultCoupons + the passed coupon. */
  skipActiveCoupons?: boolean;
};

export function useDiscountPrice(args?: UseDiscountPriceArgs) {
  const { activeSubscription } = useActiveSubscription();
  const { activeCoupons } = useActiveCoupons();

  const target = useMemo(() => {
    if (args?.targetPlan) {
      return args.targetPlan;
    }

    if (args?.useScheduledSubscriptionIfAny && activeSubscription?.scheduledSubscription) {
      return activeSubscription.scheduledSubscription;
    }

    return activeSubscription;
  }, [ activeSubscription, args ]);

  const discountPrice = useMemo(() => {
    if (args?.skipActiveCoupons) {
      return getDiscountPriceFor(target, mergeCoupons(target?.defaultCoupons, args?.coupon));
    }
    return getDiscountPriceFor(target, mergeCoupons(mergeCoupons(target?.defaultCoupons, activeCoupons), args?.coupon));
  }, [ target, activeCoupons, args ]);

  return discountPrice;
}
